import { useMemo } from 'react';

import type { StateWithoutError } from 'src/app/shared/utils/async';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { useAvailableShareChannels, useAvailableShareChannelsAsync } from '../availableChannels';
import type { ShareChannel, ShareConfig } from '../shared/types';
import { useGetShareShortUrl } from '../urls';

import { useShareActions } from './actions';

export type ShareAction<CH extends ShareChannel = ShareChannel> = {
	channel: CH;
	url: string;
	shortUrl?: string;
	/**
	 * @deprecated use `ShareAction` component property instead - this method will be removed in a future release
	 */
	action: () => Promise<boolean>;
	// FIXME can we make this type require a specific onClick prop?
	// This currently does not prevent passing components that do not accept onClick
	ShareAction: React.FC<{ children: React.ReactElement<{ onClick?: () => void }> }>;
};

export type Options<CH extends ShareChannel = ShareChannel> = {
	config?: ShareConfig;
	disableShortLinks?: boolean;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useAvailableShareActions<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{
		getUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		disableShortLinks,
		onSuccess,
		onFailure,
		config,
	}: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const configWithDefaults = useMemo(() => config || {}, [config]);

	const availableChannels = useAvailableShareChannels(channels, configWithDefaults);

	const getShortUrl = useGetShareShortUrl(availableChannels, { getUrl, disableShortLinks });

	return useShareActions(availableChannels, {
		getUrl,
		getShortUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});
}

export type AsyncResult<CH extends ShareChannel = ShareChannel> = StateWithoutError<{
	actions: ReadonlyArray<ShareAction<CH>>;
}>;

export function useAvailableShareActionsAsync<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{
		getUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		disableShortLinks,
		onSuccess,
		onFailure,
		config,
	}: Options<CH>,
): AsyncResult<CH> {
	const configWithDefaults = useMemo(() => config || {}, [config]);

	const availableChannelsState = useAvailableShareChannelsAsync(channels, configWithDefaults);

	const availableChannels = useMemo(
		() => (isLoaded(availableChannelsState) ? availableChannelsState.channels : []),
		[availableChannelsState],
	);

	const getShortUrl = useGetShareShortUrl(availableChannels, { getUrl, disableShortLinks });

	const actions = useShareActions(availableChannels, {
		getUrl,
		getShortUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});

	return useMemo(() => {
		if (isLoading(availableChannelsState)) {
			return availableChannelsState;
		}
		return { status: 'loaded', actions };
	}, [availableChannelsState, actions]);
}
